import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const AboutUs = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 60 },
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  return (
    <section className="bg-black text-white py-28 px-6 mt-[100px] overflow-hidden relative">
      <SEO
        title="About IES Gear | Premium Boxing Equipment Manufacturer"
        description="Learn about IES Gear, a premium manufacturer of export-quality boxing equipment, MMA gear, and custom sportswear from Sialkot, Pakistan."
        keywords="about IES Gear, boxing equipment manufacturer Pakistan, sportswear supplier Sialkot"
        url="https://iesgear.com/about"
      />

      {/* Background Glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-red-600/20 via-transparent to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">

        {/* Hero Heading */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-24"
        >
          <h1 className="text-5xl md:text-6xl font-orbitron font-bold tracking-wide">
            About <span className="text-red-500">IES Gear</span>
          </h1>

          <p className="mt-6 text-gray-400 max-w-2xl mx-auto text-lg">
            Precision-engineered sports gear crafted for elite performance and global standards.
          </p>
        </motion.div>

        {/* Content Sections */}
        <div className="space-y-28">

          {/* Block 1 */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={0}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.img
              whileHover={{ scale: 1.05 }}
              src="/Boxing/Categories/2.webp"
              alt="Factory"
              className="rounded-2xl shadow-2xl border border-white/10"
            />

            <div className="space-y-5">
              <h2 className="text-2xl font-semibold">Global Manufacturing Excellence</h2>
              <p className="text-gray-400 leading-relaxed">
                <strong className="text-white">IES Gear</strong>, based in Sialkot, stands at the forefront of premium boxing gear manufacturing.
                Our commitment to innovation and craftsmanship ensures world-class performance in every product.
              </p>
            </div>
          </motion.div>

          {/* Block 2 */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={1}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <div className="space-y-5 order-2 md:order-1">
              <h2 className="text-2xl font-semibold">Precision Quality Control</h2>
              <p className="text-gray-400 leading-relaxed">
                Every stage — from material sourcing to final packaging — is meticulously refined.
                Our systems guarantee durability, performance, and consistency at an international level.
              </p>
            </div>

            <motion.img
              whileHover={{ scale: 1.05 }}
              src="/Boxing/Categories/3.webp"
              alt="Quality"
              className="rounded-2xl shadow-2xl border border-white/10 order-1 md:order-2"
            />
          </motion.div>

          {/* Mission & Vision Cards */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={2}
            className="grid md:grid-cols-2 gap-8"
          >
            {/* Mission */}
            <motion.div
              whileHover={{ y: -10 }}
              className="p-8 rounded-2xl bg-white/5 backdrop-blur-lg border border-white/10"
            >
              <h3 className="text-xl font-semibold mb-4 text-white">Our Mission</h3>
              <p className="text-gray-400 leading-relaxed">
                To empower athletes with innovative, reliable, and high-performance gear that enhances their competitive edge.
              </p>
            </motion.div>

            {/* Vision */}
            <motion.div
              whileHover={{ y: -10 }}
              className="p-8 rounded-2xl bg-white/5 backdrop-blur-lg border border-white/10"
            >
              <h3 className="text-xl font-semibold mb-4 text-white">Our Vision</h3>
              <p className="text-gray-400 leading-relaxed">
                To be a globally trusted leader in sports manufacturing, delivering excellence with integrity and innovation.
              </p>
            </motion.div>
          </motion.div>

        </div>

        {/* Closing Statement */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mt-28 text-center"
        >
          <p className="text-xl text-white font-medium">
            IES Gear — engineered for champions.
          </p>
          <p className="text-gray-500 mt-2">
            Where performance meets precision and passion meets craftsmanship.
          </p>
        </motion.div>

      </div>
    </section>
  );
};

export default AboutUs;